import { useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { TPickDateAction } from '@/shared/model';
import { useReportStore } from '@/entities/report';
import type { EventRangePickersProps } from './eventRangePickers';

/**
 * Wires the jump-to and pick-on controls that set the start and end boundaries of an event range on the ECG events tab. Holds refs for the four buttons, relays picker changes to the parent, and writes jump date, jump portion and the pick-date action into the report store under the TAB_2 key.
 *
 * @param props - Picker inputs carrying the onChangePicker callback.
 * @returns An object carrying the four element refs plus the onChange, onClickJumpTo and onClickPickOn handlers.
 */
const useEventRangePickers = ({
  onChangePicker,
}: Pick<EventRangePickersProps, 'onChangePicker'>) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Stores the epoch timestamp the ECG viewer should scroll to. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New jump date, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Stores whether the viewer is currently picking the range start or the range end. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New pick-date action, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Stores which portion of the viewer window the jump target should land in. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New jump portion, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const jumpStartRef = useRef<HTMLDivElement>(null);
  const jumpEndRef = useRef<HTMLDivElement>(null);
  const pickStartRef = useRef<HTMLDivElement>(null);
  const pickEndRef = useRef<HTMLDivElement>(null);

  /**
   * Relays a date or time pick from the event range pickers up to the owning dialog. Passes the field name, moment and formatted text straight to the onChangePicker callback in props.
   *
   * @param name - Key of the picker field that changed.
   * @param time - The picked moment value.
   * @param timeString - Formatted text emitted by the picker.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any, timeString: any) => {
    onChangePicker(name, time, timeString);
  };

  /**
   * Moves the ECG viewer to the chosen boundary of the event range. Converts the date to epoch milliseconds and stores it only when it differs from the current jump date, then sets the jump portion to 0 for the start edge or 2 for the end edge.
   *
   * @param date - Boundary date to jump to, converted to epoch milliseconds.
   * @param isStart - True for the range start edge, false for the end edge.
   * @returns Nothing.
   */
  const onClickJumpTo = (date: any, isStart: boolean) => {
    const epochDate = new Date(date).getTime();
    setJumpDate((prev: any) => {
      if (epochDate && epochDate !== prev) {
        return epochDate;
      }
      return prev;
    });
    setJumpPortion(isStart ? 0 : 2);
  };

  /**
   * Arms the viewer to treat the next date pick as the start or end of the event range. Writes the matching TPickDateAction value into the report store.
   *
   * @param isStart - True to pick the range start, false to pick the end.
   * @returns Nothing.
   */
  const onClickPickOn = (isStart: boolean) => {
    setPickDateAction(isStart ? TPickDateAction.START : TPickDateAction.END);
  };

  return {
    jumpStartRef,
    jumpEndRef,
    pickStartRef,
    pickEndRef,
    onChange,
    onClickJumpTo,
    onClickPickOn,
  };
};

export default useEventRangePickers;
